import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Card, Button, Table, Tag, Modal, Space, message, Form, Input, Select } from 'antd';
import * as Icon from 'react-feather';
import { Link } from 'react-router-dom';
import {
  tagActionStart,
  tagActionError,
  getAllTagsAction,
  createTagAction,
  updateTagAction,
  deleteTagAction,
} from '../../core/data/redux/modules/tag/actions';
import { getAllTags, createTag, updateTag, deleteTag } from '../../services/adminServices/tag';

const ListTags = () => {
  const dispatch = useDispatch();
  const { tags, loading } = useSelector((state: any) => state.tag || { tags: [], loading: false });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>({ name: '', is_active: true });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line
  }, []);

  const fetchTags = async () => {
    dispatch(tagActionStart());
    try {
      const res = await getAllTags();
      if (res.error) throw new Error(res.message || 'Error fetching tags');
      const normalized = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      dispatch(getAllTagsAction(normalized));
    } catch (err: any) {
      dispatch(tagActionError(err.message));
      message.error(err.message);
    }
  };

  const handleAdd = () => {
    setEditingTag(null);
    setFormValues({ name: '', is_active: true });
    setIsModalVisible(true);
  };

  const handleEdit = (tag: any) => {
    setEditingTag(tag);
    setFormValues({ name: tag.name, is_active: tag.is_active });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteTag(id);
      dispatch(deleteTagAction(id));
      message.success('Tag deleted');
      fetchTags();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleModalOk = async () => {
    try {
      if (editingTag) {
        const res = await updateTag(editingTag.id, formValues);
        if (res.error) throw new Error(res.message);
        const updatedTag = res.data?.data || res.data;
        dispatch(updateTagAction(updatedTag));
        message.success('Tag updated');
      } else {
        const res = await createTag(formValues);
        if (res.error) throw new Error(res.message);
        const createdTag = res.data?.data || res.data;
        dispatch(createTagAction(createdTag));
        message.success('Tag created');
      }
      setIsModalVisible(false);
      fetchTags();
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Link to={`/admin/tag/tag-detail/${record.id}`}>
            <Button icon={<Icon.Eye size={16} />} size="small" />
          </Link>
          <Button icon={<Icon.Edit size={16} />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<Icon.Trash2 size={16} />} size="small" danger loading={deletingId === record.id} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/tag/list-tags">Tags</Link>
          </Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Button icon={<Icon.Plus size={16} />} type="primary" onClick={handleAdd}>
              Add Tag
            </Button>
          </Space>
          <Table
            columns={columns}
            dataSource={tags}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
        <Modal
          title={editingTag ? 'Edit Tag' : 'Add Tag'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => setIsModalVisible(false)}
          okText={editingTag ? 'Update' : 'Create'}
        >
          <Form layout="vertical">
            <Form.Item label="Name">
              <Input
                value={formValues.name}
                onChange={e => setFormValues({ ...formValues, name: e.target.value })}
                placeholder="Enter tag name"
              />
            </Form.Item>
            <Form.Item label="Status">
              <Select
                value={formValues.is_active}
                onChange={value => setFormValues({ ...formValues, is_active: value })}
                style={{ width: '100%' }}
              >
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ListTags; 